/**
 * **玩家手势 → 动作 / 动作 → 可点操作。**
 *
 * ## 这里不再走 `options()`
 *
 * 早期版本是反过来的：界面先把 `options()` 枚举出来，再把玩家拖出的牌
 * **匹配回**其中某一条 —— 于是要处理"同名牌只枚举了前几张""牌名要重匹配"
 * 这类匹配问题，本质上是在把已经展开的一棵树重新收拢。
 *
 * 现在分得很清楚：
 *
 * | | 谁用 | 怎么用 |
 * | --- | --- | --- |
 * | `options()` | **机器人 / 掉线兜底** | 枚举出每一条能改变局面的动作 |
 * | `parsePlay()` | **玩家** | 拿到手势（拖了哪几张 + 选了谁 + 选了哪个牌名）后**解析**成一条 `PlayIntent` |
 *
 * 玩家这边**不提前枚举手段**：拖出去几张、同不同名、需要不需要目标，全部从
 * `PlayerView` 直接推导（手牌 / 配置 / 弃牌堆都在视图里）。合法性只由
 * reducer 兜底 —— 界面发出去被拒就退回手牌（见各屏的 `commit`）。
 *
 * ## 这张表回答"点哪儿"
 *
 * | 局面 | 点哪儿 |
 * | --- | --- |
 * | 正常回合 | 可出牌 → 拖手牌；需目标 → 点队列里的人；结束回合 → 右下角按钮 |
 * | 响应窗口 | 拖手里的 [断点] |
 * | 濒死 | 拖手里的 Catch；放弃 → 右下角长按 |
 * | 恩惠 / 三张同名 | 拖一张手牌交出去 |
 * | 插入异常 / 捞回弃牌 | 点抽牌堆 / 点弃牌堆 |
 *
 * ## ⚠️ 这里没有一句话提示
 *
 * 界面不印"点一张牌背把它抽走"这类字：玩家能从画面本身读出来（牌背亮着、
 * 队列里有人亮着、右下角那个按钮写着动作）。这张表只回答"点哪儿"。
 */

import { isInterrupt, isPlayable, isRescue, type CardDef } from '../../core/card';
import type { CardTitle, CardUid, PlayerId } from '../../core/ids';
import type { GameAction, PlayIntent } from '../../core/actions';
import type { CardRegistry } from '../../core/pack';
import type { PlayerView } from '../../core/project';
import { cardNameResolver } from '../../core/cardText';
import { t, type TextKey, type TextParams } from '../../i18n';

/** 右下角那一个按钮：文案 + 动作。**同一时刻最多一个** */
export interface FinishAction {
  readonly label: string;
  /** hover 时说明「按下去会发生什么」 */
  readonly tip: TextKey;
  /** `tip` 里 `param` 片段的值（牌名 / 回合数…）。没有就不传 */
  readonly tipParams?: TextParams;
  readonly action: GameAction;
  /**
   * 被攻击欠下的额外回合：来源牌名。UI 拿它查牌的定义、读它的颜色，
   * 把结束键染成攻击卡的颜色。普通回合 = null。
   */
  readonly attack: { readonly source: CardTitle } | null;
}

/** 抽牌堆上的一个落点：[异常] 拖到哪，插得越靠右越靠近牌堆顶 */
export interface InsertSpot {
  /** 从底下数第几位（0 = 最底下，drawPile.length = 最顶上） */
  readonly index: number;
  readonly label: string;
  readonly action: GameAction;
}

export interface TableIntent {
  /** 现在可以点哪几个玩家（选目标）。空 = 这一阶段不选目标 */
  readonly targets: readonly PlayerId[];
  /** 抽牌堆现在是不是"选插入位置"的模式 */
  readonly insert: readonly InsertSpot[];
  /** 弃牌堆现在是不是"捞一张"的模式：uid → 动作 */
  readonly pick: ReadonlyMap<CardUid, GameAction>;
  /** 右下角那个按钮 */
  readonly finish: FinishAction | null;
  /** 这个动作要长按确认（按钮因此变红 + 按住才生效） */
  readonly confirm: boolean;
}

/** 一屏里允许出现的按钮上限。现在恒为 1 */
export const MAX_BUTTONS = 1;

const EMPTY: TableIntent = {
  targets: [],
  insert: [],
  pick: new Map(),
  finish: null,
  confirm: false,
};

// ============================================================
// 从视图里读基本信息
// ============================================================

function defOfUid(view: PlayerView, uid: CardUid, registry: CardRegistry): CardDef | undefined {
  const card = view.yourHand.find((c) => c.uid === uid);
  return card === undefined ? undefined : registry.get(card.title);
}

function aliveOthers(view: PlayerView): PlayerId[] {
  return view.players.filter((p) => p.alive && p.id !== view.you).map((p) => p.id);
}

/**
 * **拖出去的那张手牌在"不出牌"的局面里对应什么动作。**
 *
 * 覆盖三种手牌驱动的非出牌动作：
 *
 * - 濒死：把 Catch 拖出去 = 用救命牌；
 * - 恩惠 / 三张同名：把牌拖出去 = 交牌（指名时只收那张）；
 * - 响应窗口：把 [断点] 拖出去 = 打断。
 *
 * 都不是就返回 null，交给 `parsePlay` 当普通出牌解析。
 */
export function handCardAction(
  view: PlayerView,
  uid: CardUid,
  registry: CardRegistry,
): GameAction | null {
  const actor = view.you;
  const def = defOfUid(view, uid, registry);
  if (def === undefined) return null;

  const pending = view.pending;

  /**
   * ★ **[断点] 的三种打断时机，全都优先于"交出 / 被抽"：**
   *
   * 1. 响应窗口开着（别人出牌，`resolution !== null`）；
   * 2. 被三张同名**索取**（`giveCard` 抉择里，那张 [断点] 既是要交的牌、又能否决）；
   * 3. 从**被抽的人**手里抽（`stealCard` 的 `from` 是我，不是 `decision.player`）。
   *
   * 以前这几条都由 `options()` 生成 `nope` 选项、`resolvePlay` 再优先匹配；
   * 改成视图推导后必须在这里显式还原（用户报的"完全无法打断"）。
   */
  const stealFromMe =
    pending !== null && pending.kind === 'stealCard' && pending.steal?.from === actor;
  if (isInterrupt(def) && (view.resolution !== null || pending?.kind === 'giveCard' || stealFromMe)) {
    return { t: 'window/nope', actor, card: uid };
  }

  if (pending !== null && pending.player === actor) {
    if (pending.kind === 'useRescue' && isRescue(def)) {
      return { t: 'decide', actor, decision: { kind: 'useRescue', use: true } };
    }
    if (pending.kind === 'giveCard') {
      const named = pending.namedTitle ?? null;
      if (named !== null && named !== def.id) return null;
      return { t: 'decide', actor, decision: { kind: 'giveCard', card: uid } };
    }
    return null;
  }

  return null;
}

/**
 * **这手牌现在能不能选出"需要目标"的一手。**
 *
 * 判据：有需要目标的主动牌，或有两张同名（一对）。目标**候选**就是其他
 * 存活玩家 —— 具体能不能打由 reducer 判，界面不预判。
 */
export function targetableIds(view: PlayerView, registry: CardRegistry): PlayerId[] {
  const others = aliveOthers(view);
  if (others.length === 0) return [];
  const counts = new Map<CardTitle, number>();
  for (const card of view.yourHand) {
    const def = registry.get(card.title);
    if (def === undefined || !isPlayable(def)) continue;
    if (def.traits.needsTarget === true) return others;
    const n = (counts.get(card.title) ?? 0) + 1;
    if (n >= 2) return others;
    counts.set(card.title, n);
  }
  return [];
}

// ============================================================
// 手势 → 出牌意图
// ============================================================

/**
 * 玩家把手拖出去之后，这一手**够不够确定**。
 *
 * | 结果 | 意思 | 界面要他做什么 |
 * | --- | --- | --- |
 * | act | 唯一确定 | 直接打 |
 * | target | 差"对谁" | 点回合队列里的人 |
 * | name | 差"要哪个牌名"（三张同名） | 选一个牌名 |
 * | pick | 五张不同名，差"捞哪张" | 点弃牌堆里的候选 |
 * | none | 这一手不合法 | 收回手牌 |
 */
export type PlayResolution =
  | { readonly kind: 'act'; readonly action: GameAction }
  | { readonly kind: 'none' }
  | { readonly kind: 'target'; readonly targets: readonly PlayerId[] }
  | {
      readonly kind: 'name';
      readonly target: PlayerId | null;
      readonly titles: readonly CardTitle[];
    }
  | { readonly kind: 'pick'; readonly picks: ReadonlyMap<CardUid, GameAction> };

export interface PlayQuery {
  /** 已经点好的人。还没点就给 null */
  readonly target?: PlayerId | null;
  /** 已经选好的牌名。还没选就给 null */
  readonly namedTitle?: CardTitle | null;
}

/**
 * **把"拖出去的那几张"解析成一条出牌意图。**
 *
 * ⚠️ 它**不校验规则**（牌在不在手、目标活没活、张数够不够），只做分类 +
 * 补齐缺的那一步。真正的合法性判断在 reducer 里；界面发出去被拒就退回。
 */
export function parsePlay(
  view: PlayerView,
  played: readonly CardUid[],
  query: PlayQuery,
  registry: CardRegistry,
): PlayResolution {
  const actor = view.you;
  const target = query.target ?? null;
  const named = query.namedTitle ?? null;
  if (played.length === 0) return { kind: 'none' };

  const cards: { uid: CardUid; title: CardTitle; def: CardDef }[] = [];
  for (const uid of played) {
    const card = view.yourHand.find((c) => c.uid === uid);
    const def = card === undefined ? undefined : registry.get(card.title);
    if (card === undefined || def === undefined) return { kind: 'none' };
    cards.push({ uid, title: card.title, def });
  }

  const others = aliveOthers(view);
  const playAction = (play: PlayIntent): GameAction => ({ t: 'turn/play', actor, play });

  // ---- 单张 ----
  if (cards.length === 1) {
    const only = cards[0];
    if (only === undefined) return { kind: 'none' };
    const def = only.def;
    // 不能主动打出 / 单独打出去没有效果 → 不是一手合法出牌
    if (!isPlayable(def) || def.effect === null) return { kind: 'none' };
    if (def.traits.needsTarget === true) {
      if (target === null) {
        return others.length === 0 ? { kind: 'none' } : { kind: 'target', targets: others };
      }
      return { kind: 'act', action: playAction({ kind: 'single', card: only.uid, target }) };
    }
    return { kind: 'act', action: playAction({ kind: 'single', card: only.uid }) };
  }

  // ---- 一对 ----
  if (cards.length === 2) {
    if (!view.config.combos.twoOfAKind) return { kind: 'none' };
    if (cards[0]?.title !== cards[1]?.title) return { kind: 'none' };
    if (target === null) {
      return others.length === 0 ? { kind: 'none' } : { kind: 'target', targets: others };
    }
    const a = cards[0];
    const b = cards[1];
    if (a === undefined || b === undefined) return { kind: 'none' };
    return { kind: 'act', action: playAction({ kind: 'pair', cards: [a.uid, b.uid], target }) };
  }

  // ---- 三张同名 ----
  if (cards.length === 3) {
    if (!view.config.combos.threeOfAKind) return { kind: 'none' };
    if (new Set(cards.map((c) => c.title)).size !== 1) return { kind: 'none' };
    if (target === null) {
      return others.length === 0 ? { kind: 'none' } : { kind: 'target', targets: others };
    }
    if (named === null) {
      return { kind: 'name', target, titles: registry.cards.map((c) => c.id) };
    }
    const [a, b, c] = cards;
    if (a === undefined || b === undefined || c === undefined) return { kind: 'none' };
    return {
      kind: 'act',
      action: playAction({ kind: 'triple', cards: [a.uid, b.uid, c.uid], target, namedTitle: named }),
    };
  }

  // ---- 五张不同名 ----
  if (cards.length === 5) {
    if (!view.config.combos.fiveDifferent) return { kind: 'none' };
    if (new Set(cards.map((c) => c.title)).size !== 5) return { kind: 'none' };
    const uids = cards.map((c) => c.uid);
    const picks = new Map<CardUid, GameAction>();
    for (const card of view.discardPile) {
      const def = registry.get(card.title);
      if (def === undefined) continue;
      if (!view.config.combos.fiveDifferentCanTakeDefuse && isRescue(def)) continue;
      picks.set(card.uid, playAction({ kind: 'fiveDifferent', cards: uids, discardPick: card.uid }));
    }
    return picks.size > 0 ? { kind: 'pick', picks } : { kind: 'none' };
  }

  return { kind: 'none' };
}

/**
 * **把"现在松手就会打出的这一手"翻成 `turn/arm`**（举起）。
 *
 * 只在它已经**完全确定**（不差目标 / 牌名 / 捞哪张）时返回 —— 这正是
 * "只有有效拖动才开打断窗口"的判据。
 */
export function armActionOf(
  view: PlayerView,
  played: readonly CardUid[],
  registry: CardRegistry,
): GameAction | null {
  if (played.length === 0) return null;
  const done = parsePlay(view, played, {}, registry);
  if (done.kind !== 'act' || done.action.t !== 'turn/play') return null;
  return { t: 'turn/arm', actor: done.action.actor, play: done.action.play };
}

// ============================================================
// 动作 → 可点操作
// ============================================================

/**
 * **现在该让玩家点哪儿。** 纯函数：输入视图，输出纯数据。
 *
 * ⚠️ 面板**只读视图**，不读 `options()` —— 每个阶段缺什么、能点什么，
 * 视图里都写着了（`pending` / `yourDecision` / `turn` / `config`）。
 */
export function intentOf(view: PlayerView, registry: CardRegistry): TableIntent {
  const actor = view.you;
  /** 把牌 id 翻成当前语言的显示名（`tip` 里的 `param` 用） */
  const nameOf = cardNameResolver(registry);

  // ---- 抉择阶段：每个抉择有自己的"点哪儿" ----
  const pending = view.pending;
  if (pending !== null && pending.player === actor) {
    if (pending.kind === 'insertLethal') {
      const count = view.drawPileCount;
      const insert: InsertSpot[] = [];
      for (let i = 0; i <= count; i++) {
        insert.push({
          index: i,
          label: '',
          action: { t: 'decide', actor, decision: { kind: 'insertLethal', index: i } },
        });
      }
      return { ...EMPTY, insert };
    }

    if (pending.kind === 'pickFromDiscard') {
      const decision = view.yourDecision;
      const pick = new Map<CardUid, GameAction>();
      if (decision !== null && decision.kind === 'pickFromDiscard') {
        for (const uid of decision.candidates) {
          pick.set(uid, { t: 'decide', actor, decision: { kind: 'pickFromDiscard', card: uid } });
        }
      }
      return { ...EMPTY, pick };
    }

    if (pending.kind === 'stealCard') {
      // 点一张牌背（取牌扇自己处理），没有右下角按钮
      return { ...EMPTY };
    }

    if (pending.kind === 'giveCard') {
      // 指名索要 → 右下角"同意"（交出那张指定牌）；随便给 → 拖手牌
      const named = pending.namedTitle ?? null;
      if (named !== null) {
        const card = view.yourHand.find((c) => c.title === named);
        if (card !== undefined) {
          return {
            ...EMPTY,
            finish: {
              label: t('table.agree'),
              tip: 'table.agree.tip',
              tipParams: { card: nameOf(named) },
              action: { t: 'decide', actor, decision: { kind: 'giveCard', card: card.uid } },
              attack: null,
            },
          };
        }
      }
      return { ...EMPTY };
    }

    if (pending.kind === 'useRescue') {
      // 救命牌是"拖出去"；放弃送死落在右下角按钮上（变红 + 长按）
      return {
        ...EMPTY,
        confirm: true,
        finish: {
          label: t('table.giveUp'),
          tip: 'table.giveUp.tip',
          action: { t: 'decide', actor, decision: { kind: 'useRescue', use: false } },
          attack: null,
        },
      };
    }

    return { ...EMPTY };
  }

  // ---- 别人在等抉择：效果还没走完，出牌的那个人也不能结束回合 ----
  //
  // ⚠️ 以前只挡了「我自己」的抉择。当抉择在**别人**身上时（一对抽完牌轮到他
  //    调顺序、三张同名向他索要、他濒死要想……），代码会掉到下面「正常回合」
  //    那一支，于是出牌者能在自己的效果生效到一半时按结束回合 —— 用户报的
  //    「牌的生效过程没结束时可以结束回合」。任何人的抉择没走完，局面都冻着。
  if (pending !== null) return { ...EMPTY };

  // ---- 待结算出牌：打断只能靠"拖那张牌出去"，没有按钮 ----
  if (view.resolution !== null) return { ...EMPTY };

  // ---- 正常回合：目标点队列里的人；右下角"结束回合" ----
  if (view.phase !== 'playing' || view.turn?.player !== actor) return { ...EMPTY };

  const turn = view.turn;
  let attack: { source: CardTitle } | null = null;
  if (turn.fromAttack === true && turn.turnsRemaining > 1 && turn.attackSource != null) {
    attack = { source: turn.attackSource };
  }
  return {
    ...EMPTY,
    targets: targetableIds(view, registry),
    finish: {
      label: t(attack === null ? 'common.endTurn' : 'table.endExtraTurn'),
      tip: attack === null ? 'table.endTurn.tip' : 'table.endExtraTurn.tip',
      ...(attack === null
        ? {}
        : { tipParams: { card: nameOf(attack.source), count: turn.turnsRemaining } }),
      action: { t: 'turn/draw', actor },
      attack,
    },
  };
}

// ============================================================
// 手牌听不听 hover / 能不能拖
// ============================================================

/**
 * **手牌现在听不听 hover / 能不能拖。**
 *
 * 判据全部来自视图：
 *
 * | 局面 | 听不听 |
 * | --- | --- |
 * | 正常回合（能出牌） | ✅ |
 * | 响应窗口里手里有 [断点] | ✅ |
 * | 濒死：手里有救命牌 | ✅ |
 * | 恩惠：要交一张 | ✅ |
 * | 从弃牌堆捞回 / 插回异常 / 抽牌对峙 | ❌（点别处） |
 *
 * ⚠️ 不能拿"轮到我了"当唯一判据：响应窗口里不是我的回合，但我能打断。
 * ⚠️ 已经举起来的那一手必须保持可打（松手要能提交）。
 */
export function handIsLive(view: PlayerView, registry: CardRegistry, armed = false): boolean {
  if (armed) return true;
  const actor = view.you;

  if (view.resolution !== null) {
    return view.yourHand.some((card) => {
      const def = registry.get(card.title);
      return def !== undefined && isInterrupt(def);
    });
  }

  const pending = view.pending;

  // ★ 从**我**手里抽牌：`pending.player` 是抽牌人，我在 `from` 一侧 —— 手牌要活，
  //   有 [断点] 就能否决（用户报的另一种打断时机）。
  if (pending !== null && pending.kind === 'stealCard' && pending.steal?.from === actor) {
    return view.yourHand.some((card) => {
      const def = registry.get(card.title);
      return def !== undefined && isInterrupt(def);
    });
  }

  if (pending !== null && pending.player === actor) {
    if (pending.kind === 'useRescue') {
      return view.yourHand.some((card) => {
        const def = registry.get(card.title);
        return def !== undefined && isRescue(def);
      });
    }
    if (pending.kind === 'giveCard') return true;
    return false;
  }

  return view.phase === 'playing' && view.turn?.player === actor;
}

/** 动作里"用了哪几张手牌"。出牌动画的起飞点靠它 */
export function playCards(play: unknown): readonly CardUid[] {
  const p = play as { kind?: string; card?: CardUid; cards?: readonly CardUid[] };
  if (p.card !== undefined) return [p.card];
  if (Array.isArray(p.cards)) return p.cards;
  return [];
}

/** 动作里"对谁"（单张/一对/三张的目标）。没有目标就是 null */
export function targetOf(action: GameAction): PlayerId | null {
  if (action.t !== 'turn/play') return null;
  const play = action.play as { target?: PlayerId | null };
  return play.target ?? null;
}
